/**
 * AmplitudeEditor - Configuration editor for Amplitude
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Button,
  Link,
} from '@mui/material';
import type { AmplitudeConfig, PlatformConfig } from '../../types/analytics.types';
import { ANALYTICS_SETTINGS_TEST_IDS } from '@modules/analytics-settings/testIds';

interface AmplitudeEditorProps {
  config: PlatformConfig;
  onChange: (config: PlatformConfig, name?: string) => void;
  disabled?: boolean;
  readOnly?: boolean;
  showSaveButton?: boolean;
  onSave?: (config: PlatformConfig, name?: string) => void;
  name?: string;
}

export const AmplitudeEditor: React.FC<AmplitudeEditorProps> = ({
  config,
  onChange,
  disabled = false,
  readOnly = false,
  showSaveButton = false,
  onSave,
  name,
}) => {
  const amplitudeConfig = config as AmplitudeConfig;
  const [localConfig, setLocalConfig] = useState<AmplitudeConfig>(amplitudeConfig);
  const [localName, setLocalName] = useState(name || '');

  useEffect(() => {
    setLocalConfig(amplitudeConfig);
    setLocalName(name || '');
  }, [amplitudeConfig, name]);

  const handleChange = useCallback(
    (field: keyof AmplitudeConfig, value: unknown) => {
      const newConfig = { ...localConfig, [field]: value };
      setLocalConfig(newConfig);
      if (!showSaveButton) {
        onChange(newConfig, localName);
      }
    },
    [localConfig, localName, onChange, showSaveButton]
  );

  const handleSave = useCallback(() => {
    if (onSave) {
      onSave(localConfig, localName);
    }
  }, [onSave, localConfig, localName]);

  return (
    <Box>
      <TextField
        label="API Key"
        value={localConfig.apiKey || ''}
        onChange={(e) => handleChange('apiKey', e.target.value)}
        disabled={disabled || readOnly}
        fullWidth
        required
        sx={{ mb: 2 }}
        helperText={
          <>
            Find your API Key in{' '}
            <Link href="https://amplitude.com/docs/get-started" target="_blank" rel="noopener">
              Amplitude Settings
            </Link>
          </>
        }
        slotProps={{ htmlInput: { 'data-testid': ANALYTICS_SETTINGS_TEST_IDS.editorField('amplitude', 'apiKey') } }}
      />

      <FormControl fullWidth sx={{ mb: 2 }}>
        <InputLabel>Server Zone</InputLabel>
        <Select
          value={localConfig.serverZone || 'US'}
          onChange={(e) => handleChange('serverZone', e.target.value)}
          disabled={disabled || readOnly}
          label="Server Zone"
        >
          <MenuItem value="US">US (default)</MenuItem>
          <MenuItem value="EU">EU (GDPR)</MenuItem>
        </Select>
      </FormControl>

      <FormControlLabel
        control={
          <Switch
            checked={localConfig.trackSessionEvents ?? true}
            onChange={(e) => handleChange('trackSessionEvents', e.target.checked)}
            disabled={disabled || readOnly}
          />
        }
        label="Track session start/end events"
      />

      {showSaveButton && (
        <Box sx={{ mt: 3 }}>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={disabled || !localConfig.apiKey}
            data-testid={ANALYTICS_SETTINGS_TEST_IDS.editorSave('amplitude')}
          >
            Save
          </Button>
        </Box>
      )}
    </Box>
  );
};
